import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Hash, Lock, X } from "lucide-react";
import { MessageItem } from "./MessageItem";
import { Avatar } from "./Avatar";
import { useChat } from "@/lib/chat";
import { fmtDateDivider, fmtDay, sameGroup } from "@/lib/format";
import { analytics } from "@/lib/analytics";
import { useReadingTelemetry } from "@/lib/telemetry";
import dayjs from "dayjs";

const Intro = ({ c, onDetails, onAddPeople }) => {
  const isChannel = c.type === "channel";
  const other = c.type === "dm" ? c.members_info?.[0] : null;
  return (
    <div className="tc-intro" data-testid="conversation-intro">
      {isChannel ? (
        <>
          <div className="tc-intro__icon">{c.is_private ? <Lock size={26} /> : <Hash size={30} strokeWidth={2.5} />}</div>
          <h2>{c.is_private ? "🔒" : "#"} {c.name}</h2>
          <p>
            {c.created_by_info ? `@${c.created_by_info.display_name} created this channel on ${dayjs(c.created_at).format("MMMM Do, YYYY")}. ` : ""}
            This is the very beginning of the <b>#{c.name}</b> channel. {c.description || "Add a description to let people know what this channel is for."}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="tc-btn tc-btn--outline sm" onClick={onDetails} data-testid="intro-edit-description">{c.description ? "Edit description" : "Add description"}</button>
            <button className="tc-btn tc-btn--outline sm" onClick={onAddPeople} data-testid="intro-add-people">Add people</button>
          </div>
        </>
      ) : other ? (
        <>
          <Avatar user={other} size={72} radius={12} />
          <h2 style={{ marginTop: 12 }}>{other.display_name}</h2>
          <p>This conversation is just between <b>@{other.display_name}</b> and you. {other.title ? `${other.title}. ` : ""}Email: <a href={`mailto:${other.chat_email}`}>{other.chat_email}</a></p>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>{(c.members_info || []).map((u) => <Avatar key={u.id} user={u} size={48} radius={10} />)}</div>
          <h2>{c.display_name}</h2>
          <p>This is the very beginning of your group conversation with <b>{c.display_name}</b>.</p>
        </>
      )}
    </div>
  );
};

export const flashMessage = (mid, ms = 2600) => {
  const el = document.getElementById(`msg-${mid}`);
  if (!el) return false;
  el.scrollIntoView({ block: "center" });
  el.classList.add("is-flash");
  setTimeout(() => el.classList.remove("is-flash"), ms);
  return true;
};

export const MessageList = ({ conversation: c }) => {
  const { msgState, loadMessages, usersByHandle, usersById, meId, openProfile, setModal, newSince, clearNewSince, jump, clearJump, prefs } = useChat();
  const state = msgState[c.id];
  const stateMessages = state?.messages;
  const messages = useMemo(() => stateMessages || [], [stateMessages]);
  const scrollRef = useRef(null);
  useReadingTelemetry(c.id, scrollRef, messages.length);
  const dividerRef = useRef(null);
  const stick = useRef(true);
  const scrolledToNew = useRef(null);
  const handledJump = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAfter, setLoadingAfter] = useState(false);
  const [pillHidden, setPillHidden] = useState(false);
  const prevHeight = useRef(0);
  const ctx = useMemo(() => ({ usersByHandle, usersById, meId, largeEmoji: prefs.large_emoji, onMentionClick: (u) => openProfile(u.id) }), [usersByHandle, usersById, meId, openProfile, prefs.large_emoji]);

  const since = newSince[c.id];
  const firstNewId = useMemo(() => {
    if (since === undefined) return null;
    const t = since ? new Date(since).getTime() : 0;
    return messages.find((m) => m.user_id !== meId && !m.subtype && new Date(m.created_at).getTime() > t)?.id || null;
  }, [messages, since, meId]);
  const newCount = useMemo(() => {
    if (!firstNewId) return 0;
    const t = since ? new Date(since).getTime() : 0;
    return messages.filter((m) => m.user_id !== meId && !m.subtype && new Date(m.created_at).getTime() > t).length;
  }, [messages, firstNewId, since, meId]);

  useEffect(() => { stick.current = true; scrolledToNew.current = null; setPillHidden(false); const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [c.id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (loadingMore) {
      el.scrollTop = el.scrollHeight - prevHeight.current;
      setLoadingMore(false);
    } else if (loadingAfter) {
      setLoadingAfter(false);
    } else if (jump && jump.cid === c.id && !jump.pending) {
      // handled by the jump effect below
    } else if (firstNewId && scrolledToNew.current !== c.id && dividerRef.current) {
      scrolledToNew.current = c.id;
      const target = dividerRef.current.offsetTop - 72;
      const nearBottom = el.scrollHeight - target - el.clientHeight < 120;
      el.scrollTop = nearBottom ? el.scrollHeight : target;
      stick.current = nearBottom;
      analytics.track("unread_divider_shown", { category: "navigation", conversation_id: c.id, metadata: { new_messages: newCount } });
    } else if (stick.current && !state?.has_more_after) el.scrollTop = el.scrollHeight;
  }, [messages.length, c.id, firstNewId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!jump || jump.cid !== c.id || jump.pending || jump.parentId) return;
    const key = `${jump.mid}:${jump.cid}`;
    if (handledJump.current === key || !messages.some((m) => m.id === jump.mid)) return;
    handledJump.current = key;
    stick.current = false;
    scrolledToNew.current = c.id;
    requestAnimationFrame(() => { flashMessage(jump.mid); setTimeout(clearJump, 2600); });
  }, [jump, messages, c.id, clearJump]);

  const jumpToLatest = async () => {
    analytics.track("jump_to_latest_clicked", { category: "navigation", conversation_id: c.id });
    await loadMessages(c.id).catch(() => {});
    stick.current = true;
    requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; });
  };

  const [pillVisible, setPillVisible] = useState(false);
  const checkPill = () => {
    const el = scrollRef.current;
    const d = dividerRef.current;
    setPillVisible(!!(el && d && d.offsetTop < el.scrollTop + 8));
  };
  useEffect(checkPill, [messages.length, firstNewId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = async () => {
    const el = scrollRef.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80 && !state?.has_more_after;
    checkPill();
    if (el.scrollTop < 60 && state?.has_more && !loadingMore) {
      setLoadingMore(true);
      prevHeight.current = el.scrollHeight;
      await loadMessages(c.id, messages[0]?.created_at).catch(() => setLoadingMore(false));
    } else if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && state?.has_more_after && !loadingAfter && messages.length) {
      setLoadingAfter(true);
      await loadMessages(c.id, { after: messages[messages.length - 1].created_at }).catch(() => setLoadingAfter(false));
    }
  };

  const jumpToNew = () => {
    const el = scrollRef.current;
    if (el && dividerRef.current) el.scrollTo({ top: dividerRef.current.offsetTop - 72, behavior: "smooth" });
    analytics.track("unread_pill_clicked", { category: "navigation", conversation_id: c.id, metadata: { new_messages: newCount } });
  };
  const dismissPill = (e) => { e.stopPropagation(); setPillHidden(true); clearNewSince(c.id); analytics.track("unread_pill_dismissed", { category: "navigation", conversation_id: c.id }); };

  let lastDay = null;
  return (
    <div className="tc-messages">
      {firstNewId && pillVisible && !pillHidden && (
        <button className="tc-unread-pill tc-fade-in" onClick={jumpToNew} data-testid="unread-pill">
          <ArrowUp size={14} /> {newCount} new message{newCount === 1 ? "" : "s"}
          <span className="tc-unread-pill__x" onClick={dismissPill} title="Mark as read" data-testid="unread-pill-dismiss"><X size={14} /></span>
        </button>
      )}
      <div className="tc-messages__scroll tc-scroll" ref={scrollRef} onScroll={onScroll} data-testid="message-list">
        {!state?.has_more && <Intro c={c} onDetails={() => setModal({ type: "details", conversationId: c.id })} onAddPeople={() => setModal({ type: "details", conversationId: c.id, tab: "members" })} />}
        {messages.map((m, i) => {
          const day = fmtDay(m.created_at);
          const showDivider = day !== lastDay;
          lastDay = day;
          const prev = messages[i - 1];
          const isFirstNew = m.id === firstNewId;
          const grouped = !showDivider && !isFirstNew && sameGroup(prev, m) && !m.pinned;
          return (
            <Fragment key={m.id}>
              {showDivider && (
                <div className="tc-date-divider" data-testid="date-divider">
                  <button className="tc-date-divider__pill">{fmtDateDivider(m.created_at)} <ChevronDown size={14} /></button>
                </div>
              )}
              {isFirstNew && <div className="tc-new-divider" ref={dividerRef} data-testid="new-messages-divider"><span>New</span></div>}
              <MessageItem m={m} grouped={grouped} ctx={ctx} />
            </Fragment>
          );
        })}
        {state?.has_more_after && <div className="tc-more-after" data-testid="more-after">{loadingAfter ? "Loading newer messages…" : "Scroll for newer messages"}</div>}
      </div>
      {state?.has_more_after && (
        <button className="tc-unread-pill tc-unread-pill--bottom tc-fade-in" onClick={jumpToLatest} data-testid="jump-to-latest">
          <ArrowDown size={14} /> Jump to latest messages
        </button>
      )}
    </div>
  );
};
